// ─────────────────────────────────────────────────────────────
// Clickbait ERP — Auth Service (Supabase-backed)
// ─────────────────────────────────────────────────────────────
import { supabase } from '../lib/supabase'
import type { User, Session, LoginCredentials, AuthResult } from '../types/auth.types'
import { Permission } from '../types/auth.types'

// ── Map Supabase profile row → our User type ─────────────────
export function profileToUser(profile: Record<string, unknown>, email?: string): User {
  return {
    id:             profile.id as string,
    name:           (profile.full_name as string) || (email?.split('@')[0] ?? 'User'),
    email:          (profile.email as string) || email || '',
    role:           (profile.role as User['role']) || 'employee',
    founderKey:     profile.founder_key as User['founderKey'] | undefined,
    reportingTo:    profile.reporting_to as string | undefined,
    permissions:    (profile.permissions as Permission[]) || [],
    isActive:       profile.is_active !== false,
    avatarInitials: (profile.avatar_initials as string) ||
                    ((profile.full_name as string)?.[0]?.toUpperCase() ?? 'U'),
    createdAt:      profile.created_at as string,
    lastLogin:      profile.last_login as string | undefined,
  }
}

// ── Login with email + password ───────────────────────────────
export async function loginUser(credentials: LoginCredentials): Promise<AuthResult> {
  const { email, password } = credentials

  const { data, error } = await supabase.auth.signInWithPassword({ email, password })

  if (error) {
    if (error.message.includes('Invalid login credentials'))
      return { success: false, error: 'Incorrect email or password.' }
    return { success: false, error: error.message }
  }

  if (!data.user) return { success: false, error: 'Login failed. Please try again.' }

  // Fetch profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', data.user.id)
    .single()

  if (!profile) return { success: false, error: 'Profile not found. Contact admin.' }
  if (!profile.is_active) return { success: false, error: 'Your account has been deactivated.' }

  // Update last login
  await supabase.from('profiles').update({ last_login: new Date().toISOString() }).eq('id', data.user.id)

  return { success: true, user: profileToUser(profile, data.user.email) }
}

// ── Login with Google OAuth (requests Calendar scope) ─────────
export async function loginWithGoogle(): Promise<void> {
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      scopes: 'https://www.googleapis.com/auth/calendar',
      queryParams: { access_type: 'offline', prompt: 'consent' },
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  })
}

// ── Logout ────────────────────────────────────────────────────
export async function logoutUser(_user?: User | null): Promise<void> {
  await supabase.auth.signOut()
}

// ── Get current session ───────────────────────────────────────
export async function getStoredSession(): Promise<Session | null> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null

  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', session.user.id)
    .single()

  if (!profile) return null

  const user = profileToUser(profile, session.user.email)
  return {
    user,
    token:     session.access_token,
    expiresAt: new Date(session.expires_at! * 1000).getTime(),
    createdAt: Date.now(),
  }
}

// ── Permission check ──────────────────────────────────────────
export function checkPermission(user: User | null, permission: Permission): boolean {
  if (!user) return false
  if (user.role === 'superadmin') return true
  return user.permissions.includes(permission)
}

// ── Redirect path by role ─────────────────────────────────────
export function getDashboardPath(role: User['role']): string {
  switch (role) {
    case 'superadmin': return '/dashboard'
    case 'founder':    return '/founder/dashboard'
    case 'employee':   return '/employee/dashboard'
    default:           return '/dashboard'
  }
}

// ── Get all users (admin) ─────────────────────────────────────
export async function getAllUsers(): Promise<User[]> {
  const { data } = await supabase.from('profiles').select('*').order('created_at')
  return (data ?? []).map(p => profileToUser(p))
}

// ── Update user permissions ───────────────────────────────────
export async function updateUserPermissions(userId: string, permissions: Permission[]): Promise<void> {
  await supabase.from('profiles').update({ permissions }).eq('id', userId)
}

// ── Toggle user active/inactive ───────────────────────────────
export async function toggleUserActive(userId: string, isActive: boolean): Promise<void> {
  await supabase.from('profiles').update({ is_active: isActive }).eq('id', userId)
}

// ── Update profile ────────────────────────────────────────────
export async function updateUser(userId: string, updates: Partial<User>): Promise<void> {
  const dbUpdates: Record<string, unknown> = {}
  if (updates.name)           dbUpdates.full_name        = updates.name
  if (updates.role)           dbUpdates.role             = updates.role
  if (updates.permissions)    dbUpdates.permissions      = updates.permissions
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive
  await supabase.from('profiles').update(dbUpdates).eq('id', userId)
}

// ── Request password reset (sends Supabase email) ─────────────
export async function requestPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
  })
  if (error) return { success: false, error: error.message }
  return { success: true }
}

// ── Change password ───────────────────────────────────────────
export async function changePassword(newPassword: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) return { success: false, error: error.message }
  return { success: true }
}

// ── Generate strong password (kept for admin use) ─────────────
export function generateStrongPassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ', lower = 'abcdefghjkmnpqrstuvwxyz'
  const digits = '23456789', symbols = '@#$%&!'
  const all = upper + lower + digits + symbols
  const chars = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ]
  for (let i = chars.length; i < 14; i++) chars.push(all[Math.floor(Math.random() * all.length)])
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

// ── Get employees under a founder ─────────────────────────────
export async function getEmployeesForFounder(founderId: string): Promise<User[]> {
  if (!founderId) return []
  const { data } = await supabase
    .from('profiles')
    .select('*')
    .eq('reporting_to', founderId)
    .eq('role', 'employee')
    .eq('is_active', true)
  return (data ?? []).map(p => profileToUser(p))
}

// ── Legacy stubs (kept so imports don't break) ────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function logAudit(_entry?: any): void {}
export function getAuditLog() { return [] }
export function clearAuditLog(): void {}
export function getUserById(_id: string): undefined { return undefined }

// ── Admin user creation / deletion (requires Supabase Admin API) ─
// These operations need a service-role key and cannot be done from the browser.
// Users should be created via Supabase Dashboard → Authentication → Users.
export function createUser(_data: Partial<User>, _password: string): User {
  throw new Error('Creating users requires Supabase Admin API. Please add users via the Supabase Dashboard.')
}
export function deleteUser(_userId: string): void {
  throw new Error('Deleting users requires Supabase Admin API. Please delete users via the Supabase Dashboard.')
}
